import { BASKET_TYPES, addItemToBasket, removeItemFromBasket, selectBasketCheckPriceItem, selectBasketItem, selectBasketList, selectBasketToPay } from 'containers/OrderDetails/basketSlice'
import useRelatedBasketItemsGroupedByOpeningMethodKey from './hooks/useRelatedBasketItemsGroupedByOpeningMethodKey'
import { useRef, useState, useImperativeHandle, forwardRef } from 'react'
import InfoPlaceLockPopup from './popups/InfoPlaceLockPopup'
import { useReduxDispatch, useReduxSelector } from 'store'
import InfoMagLockPopup from './popups/InfoMagLockPopup'
import { useHistory, useParams } from 'react-router-dom'
import { LOCATION_STATES } from 'utils/constants'
import useTranslation from 'hooks/useTranslation'
import usePopupState from 'hooks/usePopupState'
import { displayPrice } from 'utils/string'
import RfidPopup from './popups/RfidPopup'
import LockPopup from './popups/LockPopup'
import Option from 'components/Option'
import { getIcon } from './utils'
import './style.scss'

const Locks = forwardRef((_, ref) => {
  const { id } = useParams()
  const toPay = useReduxSelector(selectBasketToPay)
  const basketList = useReduxSelector(selectBasketList)
  const selectedBasketItem = useReduxSelector(state => selectBasketItem(state, id))
  const { lock, placeLock, warehouseLockInfo, placeLockInfo, commonLockInfo } = useReduxSelector(state => selectBasketCheckPriceItem(state, id))
  const relatedBasketItemsGroupedByOpeningMethodKeyToGate = useRelatedBasketItemsGroupedByOpeningMethodKey(selectedBasketItem?.placeId)
  const relatedBasketItemsGroupedByOpeningMethodKeyToWarehouse = useRelatedBasketItemsGroupedByOpeningMethodKey(selectedBasketItem?.placeId, selectedBasketItem?.identifier)

  const { popup, pathname } = usePopupState()
  const [lockError, setLockError] = useState({
    place: false,
    mag: false
  })

  const { push } = useHistory()
  const dispatch = useReduxDispatch()
  const { t } = useTranslation('basket')

  const magLockRef = useRef(null)
  const placeLockRef = useRef(null)
  const currentBasketButtonProps = useRef({})
  currentBasketButtonProps.current = {
    lock,
    placeLock,
    basketList,
    placeLockInfo,
    commonLockInfo,
    warehouseLockInfo,
    placeId: selectedBasketItem.placeId,
    cartId: selectedBasketItem.identifier
  }

  const relatedBasketItemsGroupedByOpeningMethodKey = useRef({
    gate: relatedBasketItemsGroupedByOpeningMethodKeyToGate,
    warehouse: relatedBasketItemsGroupedByOpeningMethodKeyToWarehouse
  })
  relatedBasketItemsGroupedByOpeningMethodKey.current.gate = relatedBasketItemsGroupedByOpeningMethodKeyToGate
  relatedBasketItemsGroupedByOpeningMethodKey.current.warehouse = relatedBasketItemsGroupedByOpeningMethodKeyToWarehouse

  const thereArePlaceLocks = placeLock?.gate && placeLock?.gate?.openingMethods?.length > 0
  const thereAreWarehouseLocks = lock && lock?.openingMethods?.length > 0


  useImperativeHandle(ref, () => {
    const scrollToRef = (ref) => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth' })
      }
    }

    return (() => {
      const { basketList, lock, placeLock, warehouseLockInfo, placeLockInfo, commonLockInfo, placeId, cartId } = currentBasketButtonProps.current
      const isPlaceLockSelected = (
        commonLockInfo?.key === 'full-automatic' ||
        placeLockInfo?.key === 'full-automatic' ||
        ((placeLock?.gate && placeLock?.gate?.openingMethods?.length > 0) ? checkIfSecurityIsSelected(placeLock?.gate, basketList, placeId) : true)
      )

      const isMagLockSelected = (
        commonLockInfo?.key === 'full-automatic' ||
        warehouseLockInfo?.key === 'full-automatic' ||
        ((lock && lock?.openingMethods?.length > 0) ? checkIfSecurityIsSelected(lock, basketList, placeId, cartId) : true)
      )

      if (isPlaceLockSelected && isMagLockSelected) {
        return true
      } else {
        scrollToRef(!isPlaceLockSelected ? placeLockRef : magLockRef)
        setLockError({
          mag: !isMagLockSelected,
          place: !isPlaceLockSelected
        })
        return false
      }
    })
  }, [])

  const handleMethodClick = ({ methodObject, cartId, ownerId }) => {
    if (!methodObject.hasOptions) {
      if (cartId) {
        dispatch(removeItemFromBasket({ removeIdentifiers: [cartId] }))

      } else if (selectedBasketItem) {
        const selectedMethodObject = {
          type: BASKET_TYPES.rentAddon,
          id: methodObject.id,
          name: methodObject.name,
          object: methodObject.object,
          placeId: selectedBasketItem.placeId,
          openingMethodKey: methodObject.openingMethodKey
        }
        if (ownerId) {
          selectedMethodObject.ownerId = ownerId
          if (lockError.mag) setLockError(p => ({ ...p, mag: false }))

        } else if (lockError.place) {
          setLockError(p => ({ ...p, place: false }))
        }
        dispatch(addItemToBasket(selectedMethodObject))
      }
    } else {
      const type = methodObject.openingMethodKey === 'rfid' ? methodObject.openingMethodKey : 'lock'
      push(pathname, LOCATION_STATES.popup({ type, method: methodObject, addToGate: !ownerId }))
    }
  }

  const generateStringListOfSelectedItems = (selectedItems) => {
    if (!selectedItems) return
    const groupedItems = selectedItems.reduce((r, { name, amount, objectId, identifier }) => {
      if (r[objectId]) {
        r[objectId].amount += amount
      } else {
        r[objectId] = {
          amount,
          name,
          price: toPay?.[identifier]?.to_pay?.price_sum
        }
      }
      return r
    }, {})
    return Object.values(groupedItems).reduce((r, e, i) => r += e.price && Number(e.price) ? `${i ? ', ' : ''}${e.amount > 1 ? `${e.amount} x ` : ''}${e.name} (${displayPrice(e.price)})` : '', '')
  }

  return (
    <>
      {popup === 'info_mag_lock' && <InfoMagLockPopup
        body={lock}
      />}
      {popup === 'info_place_lock' && <InfoPlaceLockPopup
        body={placeLock}
      />}
      {(popup?.type === 'rfid' || popup?.type === 'lock') && <>
        {popup.type === 'rfid'
          ? <RfidPopup
            method={popup?.method}
            addToGate={popup?.addToGate}
          /> : <LockPopup
            method={popup?.method}
            addToGate={popup?.addToGate}
            selectedItems={
              popup?.addToGate
                ? relatedBasketItemsGroupedByOpeningMethodKeyToGate?.[popup?.method?.openingMethodKey]
                : relatedBasketItemsGroupedByOpeningMethodKeyToWarehouse?.[popup?.method?.openingMethodKey]
            }
          />}
      </>}

      {commonLockInfo
        ? <div className='locks'>
          <div className='locks__section'>
            <div className='title'>
              <div className='group'>
                <span>{t('howToOpenTheYardAndWarehouse')}</span>
              </div>
            </div>
            <div className='content'>
              <Option
                selected
                dashedBorder
                icon='shield cPrimary'
                title={commonLockInfo.title}
                desc={commonLockInfo.description}
                additionalContent={<div
                  className='option__additional__desc'
                  dangerouslySetInnerHTML={{ __html: commonLockInfo.howTo }}
                />}
              />
              {placeLock?.gate && placeLock?.gate?.openingMethods?.length > 0 && <>
                <p>{t('additional')}</p>
                {placeLock.gate && placeLock.gate.openingMethods && placeLock.gate.openingMethods.map((method) => {
                  const group = relatedBasketItemsGroupedByOpeningMethodKeyToGate?.[method.openingMethodKey]
                  const description = generateStringListOfSelectedItems(group)
                  const cartId = group?.at(-1)?.identifier
                  return <div
                    key={method.id}
                    className={`lockItem ${cartId ? 'checked' : ''}`}
                    onClick={() => {
                      handleMethodClick({ methodObject: method, cartId })
                    }}>
                    <div className='lockItem__left'>
                      <div className='iconWrap'>
                        <i className={`icon icon--${getIcon(method.openingMethodKey)}`} />
                      </div>
                    </div>
                    <div className='lockItem__center'>
                      <h3>{cartId ? method.name : method.nameForClient}</h3>
                      <span>{description || method.descriptionForClient}</span>
                    </div>
                    <div className='lockItem__right'>
                      <div className='checkCircle'><i className='icon icon--check'></i></div>
                    </div>
                  </div>
                })}
              </>}
            </div>
          </div>
        </div>
        : <div className='locks'>
          {(thereArePlaceLocks || placeLockInfo) && <div
            ref={placeLockRef}
            className='locks__section'>
            <div className='title'>
              <div className='group'>
                <span>{t('howToOpenTheYard')}</span>
                {thereArePlaceLocks && <i
                  className='icon icon--quest'
                  onClick={() => {
                    push(pathname, LOCATION_STATES.popup('info_place_lock'))
                  }}
                />}
              </div>
              <span className={`error${lockError.place ? ' active' : ''}`}>{t('chooseHowToOpen')}</span>
            </div>
            <div className='content'>
              {placeLockInfo && <Option
                icon='gate s32'
                title={placeLockInfo.title}
                desc={placeLockInfo.description}
                additionalContent={<div
                  className='option__additional__desc'
                  dangerouslySetInnerHTML={{ __html: placeLockInfo.howTo }}
                />}
              />}
              {thereArePlaceLocks && <>
                {placeLockInfo?.key === 'full-automatic' && <p>{t('additional')}</p>}
                {placeLock?.gate?.openingMethods?.map((method) => {
                  const group = relatedBasketItemsGroupedByOpeningMethodKeyToGate?.[method.openingMethodKey]
                  const description = generateStringListOfSelectedItems(group)
                  const cartId = group?.at(-1)?.identifier
                  return <div
                    key={method.id}
                    className={`lockItem ${cartId ? 'checked' : ''}`}
                    onClick={() => {
                      handleMethodClick({ methodObject: method, cartId })
                    }}>
                    <div className='lockItem__left'>
                      <div className='iconWrap'>
                        <i className={`icon icon--${getIcon(method.openingMethodKey)}`} />
                      </div>
                    </div>
                    <div className='lockItem__center'>
                      <h3>{cartId ? method.name : method.nameForClient}</h3>
                      <span>{description || method.descriptionForClient}</span>
                    </div>
                    <div className='lockItem__right'>
                      <div className='checkCircle'><i className='icon icon--check'></i></div>
                    </div>
                  </div>
                })}
              </>}
            </div>
          </div>}

          {(thereAreWarehouseLocks || warehouseLockInfo) && <div
            ref={magLockRef}
            className='locks__section'>
            <div className='title'>
              <div className='group'>
                <span>{t('howToOpenTheWarehouse')}</span>
                {thereAreWarehouseLocks && <i
                  className='icon icon--quest'
                  onClick={() => {
                    push(pathname, LOCATION_STATES.popup('info_mag_lock'))
                  }}
                />}
              </div>
              <span className={`error${lockError.mag ? ' active' : ''}`}>{t('chooseHowToOpen')}</span>
            </div>
            <div className='content'>
              {warehouseLockInfo && <Option
                icon='mag-details'
                title={warehouseLockInfo.title}
                desc={warehouseLockInfo.description}
                additionalContent={<div
                  className='option__additional__desc'
                  dangerouslySetInnerHTML={{ __html: warehouseLockInfo.howTo }}
                />}
              />}
              {thereAreWarehouseLocks && <>
                {warehouseLockInfo?.key === 'full-automatic' && <p>{t('additional')}</p>}
                {lock?.openingMethods?.map((method) => {
                  const group = relatedBasketItemsGroupedByOpeningMethodKeyToWarehouse?.[method.openingMethodKey]
                  const description = generateStringListOfSelectedItems(group)
                  const cartId = group?.at(-1)?.identifier
                  return <div
                    key={method.id}
                    className={`lockItem ${cartId ? 'checked' : ''}`}
                    onClick={() => {
                      handleMethodClick({ methodObject: method, cartId, ownerId: selectedBasketItem.identifier })
                    }}>
                    <div className='lockItem__left'>
                      <div className='iconWrap'>
                        <i className={`icon icon--${getIcon(method.openingMethodKey)}`} />
                      </div>
                    </div>
                    <div className='lockItem__center'>
                      <h3>{cartId ? method.name : method.nameForClient}</h3>
                      <span>{description || method.descriptionForClient}</span>
                    </div>
                    <div className='lockItem__right'>
                      <div className='checkCircle'>
                        <i className='icon icon--check' />
                      </div>
                    </div>
                  </div>
                })}
              </>}
            </div>
          </div>}
        </div>}
    </>)
})

const checkIfSecurityIsSelected = (locks, list, placeId, cartId) => {
  if (!locks) return true
  for (const item of list) {
    if (item.type === 'rentAddon' && (
      item.ownerId
        ? item.ownerId === cartId
        : item.related
          ? (item.related.some(r => cartId ? r.cartId === cartId : r.id === placeId))
          : (!cartId && item.placeId === placeId)
    )) {
      return true
    }
  }
  return false
}

export default Locks